import { Fragment, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import EventsPreview from '../components/sections/EventsPreview';
import CyberLunaWindow from '../components/base/CyberLunaWindow';
import XPVideoPlayer from '../components/base/XPVideoPlayer';
import Seo from '../components/base/Seo';
import styles from './Story.module.css';
import content from '../data/content.json';

const s = content.story;

// Matches the two-column breakpoint of .contentGrid — section videos get the
// XPVideoPlayer window chrome on desktop, a plain video on stacked mobile.
const DESKTOP_QUERY = '(min-width: 992px)';

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
};

/**
 * Story Page - The Boombox Origin Story
 *
 * @component
 * @description
 * Data-driven photo-essay. Hero → intro lead-in → alternating text+photo
 * timeline sections (each an XP window) with pull quotes lifted from the
 * copy between sections → closing CTA. Content lives in content.json (story).
 */
const Story = () => {
  const isDesktop = useIsDesktop();

  // Animation variants for scroll-triggered reveals
  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <div className={styles.storyPage}>
      <Seo pageKey="story" />

      {/* Top banner — hero + intro share one continuous background image */}
      <div className={styles.topBanner} style={{ '--hero-image': `url(${process.env.PUBLIC_URL}/images/building.jpeg)` }}>
      {/* ========================================
          HERO SECTION
          ======================================== */}
      <motion.section
        className={styles.hero}
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        <h1 className={styles.heroTitle}>{s.hero.title}</h1>
        <p className={styles.heroSubtitle}>{s.hero.subtitle}</p>
        <div className={styles.heroAccent}></div>
      </motion.section>

      {/* ========================================
          INTRO LEAD-IN
          ======================================== */}
      <motion.section
        className={styles.intro}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
        variants={fadeInUp}
      >
        <div className={styles.introInner}>
          {s.intro.map((p, i) => (
            <p key={i} className={styles.introText}>{p}</p>
          ))}
        </div>
      </motion.section>
      </div>

      {/* ========================================
          TIMELINE SECTIONS (data-driven)
          ======================================== */}
      {s.sections.map((section, index) => (
        <Fragment key={section.title}>
          <motion.section
            className={`${styles.timelineSection} ${index % 2 === 1 ? styles.alternate : ''}`}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
          >
            <div className={styles.container}>
              <CyberLunaWindow isStatic title={section.window_title} bodyClassName={styles.windowContent}>
                <div className={styles.contentGrid}>
                  <div className={styles.textContent}>
                    <h2 className={styles.sectionTitle}>{section.title}</h2>
                    <div className={styles.accentLine}></div>
                    {section.paragraphs.map((p, i) => {
                      const isLast = i === section.paragraphs.length - 1;
                      return (
                        <p key={i} className={styles.bodyText}>
                          {section.emphasize_last && isLast ? <strong>{p}</strong> : p}
                        </p>
                      );
                    })}
                  </div>
                  <div className={styles.imageContainer}>
                    {section.video ? (
                      isDesktop ? (
                        <XPVideoPlayer
                          src={`${process.env.PUBLIC_URL}/${section.video}#t=0.5`}
                          label={section.video_label}
                        />
                      ) : (
                        <video
                          src={`${process.env.PUBLIC_URL}/${section.video}#t=0.5`}
                          aria-label={section.video_label}
                          className={`${styles.sectionImage} ${styles.sectionVideo}`}
                          controls
                          preload="metadata"
                          playsInline
                        />
                      )
                    ) : (
                      <img
                        src={`${process.env.PUBLIC_URL}/${section.image}`}
                        alt={section.image_alt}
                        className={styles.sectionImage}
                      />
                    )}
                    <div className={styles.imageGlow}></div>
                  </div>
                </div>
              </CyberLunaWindow>
            </div>
          </motion.section>

          {section.pull_quote && (
            <motion.section
              className={styles.pullQuoteSection}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div className={styles.container}>
                <blockquote className={styles.pullQuote}>
                  <span className={styles.quoteIcon} aria-hidden="true">&ldquo;</span>
                  <p className={styles.quoteText}>{section.pull_quote.text}</p>
                  <footer className={styles.quoteAuthor}>{section.pull_quote.author}</footer>
                </blockquote>
              </div>
            </motion.section>
          )}
        </Fragment>
      ))}

      {/* Events — embedded Shotgun widget, same as the home page */}
      <EventsPreview />
    </div>
  );
};

export default Story;
